import React, { useState, useMemo } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight, FaCircle } from 'react-icons/fa';
import { SessionCard } from './SessionCard';
import { calculateTotalDurationByDay } from '../utils/timeCalculations';

interface Session {
    id: string;
    startedAt: string;
    endedAt: string | null;
    lastInteraction: string;
    createdAt: string;
    updatedAt: string;
}

interface SessionCalendarProps {
    sessions: Session[];
}

export const SessionCalendar: React.FC<SessionCalendarProps> = ({ sessions }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    // Agrupar sesiones por día
    const sessionsByDay = useMemo(() => {
        const grouped = new Map<string, Session[]>();
        sessions.forEach(session => {
            const date = new Date(session.startedAt).toISOString().split('T')[0];
            if (!grouped.has(date)) {
                grouped.set(date, []);
            }
            grouped.get(date)?.push(session);
        });
        return grouped;
    }, [sessions]);

    // Generar días del mes actual
    const getDaysInMonth = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        // Agregar días vacíos al principio para alinear con el día de la semana correcto
        for (let i = 0; i < firstDay.getDay(); i++) {
            days.push(null);
        }

        // Agregar los días del mes
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    };

    const days = getDaysInMonth();
    const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
        setSelectedDate(null);
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
        setSelectedDate(null);
    };

    const getSessionsForDate = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        return sessionsByDay.get(dateStr) || [];
    };

    return (
        <div>
            <Card className="mb-4 border-secondary">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <Button variant="outline-secondary" onClick={handlePrevMonth}>
                            <FaChevronLeft />
                        </Button>
                        <h5 className="mb-0">
                            {currentDate.toLocaleString('es-AR', { month: 'long', year: 'numeric' })}
                        </h5>
                        <Button variant="outline-secondary" onClick={handleNextMonth}>
                            <FaChevronRight />
                        </Button>
                    </div>

                    <div className="calendar">
                        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
                            {weekDays.map(day => (
                                <div key={day} className="text-center text-secondary p-2">
                                    {day}
                                </div>
                            ))}
                            {days.map((day, index) => {
                                if (!day) return <div key={`empty-${index}`} />;

                                const dateStr = day.toISOString().split('T')[0];
                                const daySessions = getSessionsForDate(day);
                                const hasActiveSessions = daySessions.some(s => !s.endedAt);
                                const isSelected = dateStr === selectedDate;

                                return (
                                    <Button
                                        key={dateStr}
                                        variant={isSelected ? "primary" : "dark"}
                                        className={`position-relative p-2 ${daySessions.length ? '' : 'opacity-50'}`}
                                        onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                                        disabled={!daySessions.length}
                                    >
                                        <div className="d-flex justify-content-center gap-2 align-items-center position-relative">
                                            <span>{day.getDate()}</span>
                                            {daySessions.length > 0 && (
                                                <div
                                                    className="d-flex position-absolute translate-middle align-items-center justify-content-center"
                                                    style={{
                                                        top: '50%',
                                                        left: '95%',
                                                    }}
                                                >
                                                    <FaCircle
                                                        size={8}
                                                        className={hasActiveSessions ? 'text-success' : 'text-secondary'}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {daySessions.length > 0 && (
                                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                                                {daySessions.length}
                                            </span>
                                        )}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {selectedDate && sessionsByDay.get(selectedDate) && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 bg-dark rounded-4 px-4 py-3 w-100">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="mb-0 text-white">
                                    {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-AR', {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long'
                                    }).charAt(0).toUpperCase() + new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-AR', {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long'
                                    }).slice(1)}
                                </h5>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-secondary">Sesiones:</span>
                                    <span className="badge bg-secondary px-3 py-2">
                                        {sessionsByDay.get(selectedDate)?.length || 0}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-secondary">Tiempo total:</span>
                                    <span className="badge bg-primary px-3 py-2">
                                        {calculateTotalDurationByDay(sessionsByDay.get(selectedDate) || [])}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {sessionsByDay.get(selectedDate)?.map(session => (
                        <SessionCard key={session.id} session={session} />
                    ))}
                </div>
            )}
        </div>
    );
}; 